import { useEffect, useState, useContext } from "react"
import axios from "axios"
import { AuthContext } from "../context/AuthContext"
import { useNotificationContext } from "../context/NotificationContext"
import { CheckCircle } from "lucide-react"
import { showToast } from "../utils/toastUtils"
import { format } from "date-fns"
const BASE_URL = process.env.REACT_APP_API_URL;

const NotificationPopup = ({ onClose }) => {
  const { token } = useContext(AuthContext)
  const { fetchUnreadStatus } = useNotificationContext()
  const [notifications, setNotifications] = useState([])

  if (token) {
    axios.defaults.headers.common["x-auth-token"] = token
  }

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/notifications`)
        setNotifications(res.data)
      } catch (err) {
        showToast("Error", "Failed to fetch notifications", "error")
      }
    }

    fetchNotifications()
  }, [])

  const handleMarkAsRead = async (id) => {
    try {
      await axios.patch(`${BASE_URL}/api/notifications/${id}/read`)
      // Optimistic update
      setNotifications((prev) => prev.map(n => n._id === id ? { ...n, isRead: true } : n))
      await fetchUnreadStatus()
      // No toast intentionally
    } catch (err) {
      console.error(err)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      if (notifications.every(n => n.isRead)) return // Avoid call if already read
      await axios.patch(`${BASE_URL}/api/notifications/read-all`)
      setNotifications((prev) => prev.map(n => ({ ...n, isRead: true })))
      await fetchUnreadStatus()
      showToast("Success", "All marked as read", "success")
    } catch (err) {
      showToast("Error", "Failed to update notifications", "error")
    }
  }

  const handleClearAll = async () => {
    try {
      if (notifications.length === 0) return
      await axios.delete(`${BASE_URL}/api/notifications`)
      setNotifications([])
      await fetchUnreadStatus()
      showToast("Success", "Notifications cleared", "success")
    } catch (err) {
      showToast("Error", "Failed to clear notifications", "error")
    }
  }

  return (
    <div className="absolute right-0 mt-2 w-[400px] max-h-[500px] flex flex-col z-50 bg-white border border-gray-200 rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 rounded-t-xl">
        <h3 className="font-semibold text-gray-800">Notifications</h3>
        <div className="flex gap-2">
          {notifications.some(n => !n.isRead) && (
            <button onClick={handleMarkAllRead} className="text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50 transition">
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={handleClearAll} className="text-xs font-medium text-gray-500 hover:text-red-600 px-2 py-1 rounded hover:bg-gray-100 transition">
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="bg-gray-100 p-3 rounded-full mb-3">
              <CheckCircle className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm">All caught up! No new notifications.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {notifications.map((notification) => (
              <li
                key={notification._id}
                onClick={() => {
                  if (notification.link) {
                    window.location.href = notification.link;
                  }
                }}
                className={`p-3 rounded-lg flex items-start gap-3 transition-colors cursor-pointer ${notification.isRead ? "bg-white" : "bg-blue-50"
                  } hover:bg-gray-50`}
              >
                <div className="flex-1 min-w-0">
                  <p className={`text-sm text-gray-800 leading-snug ${notification.isRead ? 'font-normal' : 'font-medium'}`}>
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                    {format(new Date(notification.createdAt), 'MMM d, h:mm a')}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {!notification.isRead && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification._id);
                      }}
                      className="w-2.5 h-2.5 bg-blue-600 rounded-full hover:bg-blue-700 transition"
                      title="Mark as read"
                    />
                  )}
                </div>
              </li>
            ))}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-100 bg-gray-50 flex justify-between items-center rounded-b-xl">
        <button
          onClick={handleMarkAllRead}
          disabled={notifications.every(n => n.isRead)}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          Mark all read
        </button>
        <button
          onClick={handleClearAll}
          disabled={notifications.length === 0}
          className="text-xs font-medium text-red-500 hover:text-red-600 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          Clear all
        </button>
      </div>
    </div>
  )
}

export default NotificationPopup
